import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';

type Integ = {
  id: string;
  provider: string;
  displayName: string | null;
  virtualAgentId: string | null;
};

type TemplateRow = {
  id: string;
  virtualAgentId: string;
  channelIntegrationId: string;
  templateName: string;
  languageCode: string;
  action: string;
  isActive: boolean;
  channel: { id: string; displayName: string | null; provider: string } | null;
};

const ACTION_WHATSAPP_24H_LOCK = 'whatsapp_24h_lock';
const ACTION_LABELS: Record<string, string> = {
  [ACTION_WHATSAPP_24H_LOCK]: 'Bloqueo de ventana 24 h WhatsApp',
};

const LANGUAGES = ['es', 'es_AR', 'es_MX', 'en_US', 'pt_BR'];

function channelLabel(row: Integ | TemplateRow['channel']): string {
  if (!row) return 'Canal';
  const name = 'displayName' in row ? row.displayName?.trim() : '';
  return name || (row.provider === 'whatsapp_meta' ? 'WhatsApp Cloud' : row.provider);
}

export function WhatsappTemplatesPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { selectedAgentId, selectedAgent } = useAgentSelector();
  const { can } = useTenantPermissions();
  const canWrite = can('web.agents.write');

  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [channels, setChannels] = useState<Integ[]>([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const [channelId, setChannelId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [languageCode, setLanguageCode] = useState('es');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!selectedAgentId) {
      setRows([]);
      setChannels([]);
      return;
    }
    setErr('');
    setLoading(true);
    try {
      const [tpls, integs] = await Promise.all([
        apiGet<TemplateRow[]>(`/api/console/agents/${selectedAgentId}/whatsapp-templates`, token),
        apiGet<Integ[]>(`/api/console/integrations?agentId=${selectedAgentId}`, token),
      ]);
      setRows(tpls);
      const wa = integs.filter((i) => i.provider === 'whatsapp_meta');
      setChannels(wa);
      setChannelId((prev) => (prev && wa.some((c) => c.id === prev) ? prev : wa[0]?.id ?? ''));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedAgentId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedAgentId || !channelId || !templateName.trim()) return;
    setCreating(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/agents/${selectedAgentId}/whatsapp-templates`,
        {
          channelIntegrationId: channelId,
          templateName: templateName.trim(),
          languageCode,
          action: ACTION_WHATSAPP_24H_LOCK,
          isActive: true,
        },
        token,
        { successMessage: 'Plantilla asociada' }
      );
      setTemplateName('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (row: TemplateRow) => {
    if (!selectedAgentId) return;
    try {
      await apiPatch(
        `/api/console/agents/${selectedAgentId}/whatsapp-templates/${row.id}`,
        { isActive: !row.isActive },
        token,
        { successMessage: row.isActive ? 'Plantilla desactivada' : 'Plantilla activada' }
      );
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const remove = async (row: TemplateRow) => {
    if (!selectedAgentId) return;
    if (!window.confirm(`¿Quitar «${row.templateName}»?`)) return;
    try {
      await apiDelete(`/api/console/agents/${selectedAgentId}/whatsapp-templates/${row.id}`, token, {
        successMessage: 'Plantilla eliminada',
      });
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  return (
    <div style={pageShell}>
      <div style={pageScroll}>
        <div style={pageHeaderRow}>
          <div>
            <h1 style={pageTitle}>Plantillas</h1>
            <p style={pageSubtitle}>
              Asociá plantillas de WhatsApp Cloud a este agente y a una acción. «Bloqueo de ventana 24 h» sustituye
              el área de redacción en la PWA cuando el contacto no escribió en 24 horas.
            </p>
          </div>
          <button type="button" onClick={() => void load()} style={btnGhost}>
            ⟳ Actualizar
          </button>
        </div>

        {!selectedAgentId ? (
          <div style={emptyState}>
            <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>
              Seleccioná un agente arriba para ver y cargar sus plantillas.
            </p>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
              Agente: <strong style={{ color: '#0f172a' }}>{selectedAgent?.name ?? selectedAgentId}</strong>
            </p>

            {err ? (
              <div role="alert" style={errBox}>
                {err}
              </div>
            ) : null}

            {canWrite ? (
              <form onSubmit={(e) => void onCreate(e)} style={addForm}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 10 }}>Nueva asociación</div>
                {channels.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                    Este agente no tiene un canal WhatsApp Cloud. Configuralo en{' '}
                    <Link to="/app/integrations" style={{ color: '#0369a1', fontWeight: 600 }}>
                      Canales
                    </Link>
                    .
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <label style={fieldLabel}>
                      Canal
                      <select style={input} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                        {channels.map((c) => (
                          <option key={c.id} value={c.id}>
                            {channelLabel(c)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={fieldLabel}>
                      Nombre en Meta
                      <input
                        style={input}
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="hello_world"
                        required
                      />
                    </label>
                    <label style={fieldLabel}>
                      Idioma
                      <select style={input} value={languageCode} onChange={(e) => setLanguageCode(e.target.value)}>
                        {LANGUAGES.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={fieldLabel}>
                      Acción
                      <select style={input} value={ACTION_WHATSAPP_24H_LOCK} disabled>
                        <option value={ACTION_WHATSAPP_24H_LOCK}>{ACTION_LABELS[ACTION_WHATSAPP_24H_LOCK]}</option>
                      </select>
                    </label>
                  </div>
                )}
                {channels.length > 0 ? (
                  <div style={{ marginTop: 12 }}>
                    <button type="submit" style={btnPri} disabled={creating || !templateName.trim()}>
                      {creating ? 'Guardando…' : 'Asociar plantilla'}
                    </button>
                  </div>
                ) : null}
              </form>
            ) : null}

            {loading && rows.length === 0 ? (
              <div style={emptyState}>
                <p style={{ margin: 0, color: '#475569' }}>Cargando…</p>
              </div>
            ) : rows.length === 0 ? (
              <div style={emptyState}>
                <p style={{ margin: 0, color: '#475569' }}>No hay plantillas para este agente.</p>
              </div>
            ) : (
              <div style={card}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Plantilla</th>
                      <th style={th}>Idioma</th>
                      <th style={th}>Canal</th>
                      <th style={th}>Acción</th>
                      <th style={th}>Estado</th>
                      {canWrite ? <th style={th} /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td style={td}>
                          <code style={{ fontSize: 13 }}>{row.templateName}</code>
                        </td>
                        <td style={td}>{row.languageCode}</td>
                        <td style={td}>{channelLabel(row.channel)}</td>
                        <td style={td}>{ACTION_LABELS[row.action] ?? row.action}</td>
                        <td style={td}>{row.isActive ? 'Activa' : 'Inactiva'}</td>
                        {canWrite ? (
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <button type="button" style={btnSm} onClick={() => void toggleActive(row)}>
                              {row.isActive ? 'Desactivar' : 'Activar'}
                            </button>
                            <button type="button" style={btnSm} onClick={() => void remove(row)}>
                              Quitar
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const pageShell: CSSProperties = { minHeight: '100%', background: '#f1f5f9' };
const pageScroll: CSSProperties = { maxWidth: 1100, margin: '0 auto', padding: '28px 24px 48px' };
const pageHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 16,
};
const pageTitle: CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: '-0.02em',
  color: '#0f172a',
};
const pageSubtitle: CSSProperties = { margin: '8px 0 0', fontSize: 14, color: '#64748b', lineHeight: 1.5, maxWidth: 720 };
const emptyState: CSSProperties = {
  padding: 32,
  textAlign: 'center',
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
};
const errBox: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 10,
  background: '#fef2f2',
  color: '#b91c1c',
  fontSize: 14,
  marginBottom: 12,
};
const addForm: CSSProperties = {
  marginBottom: 14,
  padding: 14,
  borderRadius: 12,
  background: '#fff',
  border: '1px solid #e2e8f0',
};
const card: CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
};
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#94a3b8',
  fontWeight: 700,
};
const td: CSSProperties = { padding: '10px', verticalAlign: 'middle', borderTop: '1px solid #e2e8f0' };
const fieldLabel: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155' };
const input: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 13,
  boxSizing: 'border-box',
};
const btnGhost: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#475569',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};
const btnPri: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 10,
  border: 'none',
  background: '#0ea5e9',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSm: CSSProperties = {
  padding: '6px 10px',
  marginRight: 6,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};
